/**
 * Carries a client switch's landing path across the hard navigation, so a settings section the new
 * client hides is left quietly rather than reported as a link the person opened.
 */
import { nowInstant } from '@insio/dates';

/** sessionStorage key: `{ path, at }` of the last client switch. */
export const CLIENT_SWITCH_LANDING_KEY = 'account_clientSwitchLanding';

/** How long after the switch its landing may be taken; an older one is a stale leftover. */
export const CLIENT_SWITCH_LANDING_TTL_MS = 60_000;

interface ClientSwitchLanding {
  readonly path: string;
  readonly at: number;
}

function isLanding(value: unknown): value is ClientSwitchLanding {
  return (
    typeof value === 'object' &&
    value !== null &&
    'path' in value &&
    typeof value.path === 'string' &&
    'at' in value &&
    typeof value.at === 'number'
  );
}

/** Records the path a client switch is about to reload into. */
export function rememberClientSwitchLanding(path: string): void {
  const landing: ClientSwitchLanding = { path, at: nowInstant().epochMilliseconds };
  try {
    sessionStorage.setItem(CLIENT_SWITCH_LANDING_KEY, JSON.stringify(landing));
  } catch {
    // Without storage the landing simply says why, as any denied visit does.
  }
}

/** True when `pathname` is a fresh client switch's landing; clears the record either way. */
export function takeClientSwitchLanding(pathname: string): boolean {
  try {
    const raw = sessionStorage.getItem(CLIENT_SWITCH_LANDING_KEY);
    if (raw === null) return false;
    sessionStorage.removeItem(CLIENT_SWITCH_LANDING_KEY);
    const landing: unknown = JSON.parse(raw);
    return (
      isLanding(landing) &&
      landing.path === pathname &&
      nowInstant().epochMilliseconds - landing.at <= CLIENT_SWITCH_LANDING_TTL_MS
    );
  } catch {
    return false;
  }
}
